/**
 * Space Store
 *
 * Manages the current space context and the user's available spaces.
 * A space is a bounded working context (personal, team, org) within a workspace.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { MySpace, SpaceMembershipRole, SpaceType } from '../../types/prioritization/collaborationSpaces';
import { isAuthenticated } from '../../lib/supabaseSync';
import {
  getMySpacesResult,
  addSpaceMember,
  updateSpaceMemberRole,
  removeSpaceMember,
  ensurePersonalSpace,
  ensureOrgSpace,
  createSpace,
  updateSpace,
  deleteSpace,
  type CreateSpaceParams,
  type MembershipMutationResult,
} from '../../lib/spaceService';

// ── State ──────────────────────────────────────────────────────────────────────

interface SpaceStoreState {
  // The space the user is currently working in. null = not yet resolved.
  currentSpaceId: string | null;

  // All spaces the user belongs to (within the current workspace).
  mySpaces: MySpace[];

  isLoading: boolean;
  hasFetched: boolean;

  /**
   * Lifecycle of the one-shot space bootstrap for the current workspace.
   * 'idle'/'running' mean "not resolved yet" — the switcher must render a
   * loading affordance, NOT the empty "Select a space…" prompt, which is
   * indistinguishable from a genuine no-selection state.
   */
  bootstrapStatus: BootstrapStatus;
  /** Workspace the current bootstrapStatus refers to. */
  bootstrapWorkspaceId: string | null;

  // ── Getters ────────────────────────────────────────────────────────────────
  getCurrentSpace: () => MySpace | undefined;
  getSpacesByType: (type: SpaceType) => MySpace[];
  getPersonalSpace: () => MySpace | undefined;
  getOrgSpace: () => MySpace | undefined;

  // ── Context actions ────────────────────────────────────────────────────────
  setCurrentSpace: (spaceId: string | null) => void;

  // ── Data actions ───────────────────────────────────────────────────────────
  fetchSpaces: (workspaceId: string) => Promise<void>;
  bootstrapSpaces: (workspaceId: string) => Promise<void>;
  /** Force a fresh bootstrap attempt after a failure (user-initiated retry). */
  retryBootstrap: (workspaceId: string) => Promise<void>;

  // ── Space CRUD ─────────────────────────────────────────────────────────────
  createSpace: (params: CreateSpaceParams) => Promise<MySpace | null>;
  updateSpace: (spaceId: string, updates: Parameters<typeof updateSpace>[1]) => Promise<boolean>;
  deleteSpace: (spaceId: string) => Promise<boolean>;

  // ── Membership ─────────────────────────────────────────────────────────────
  addMember: (spaceId: string, userId: string, role: SpaceMembershipRole) => Promise<MembershipMutationResult>;
  updateMemberRole: (spaceId: string, userId: string, role: SpaceMembershipRole) => Promise<MembershipMutationResult>;
  removeMember: (spaceId: string, userId: string) => Promise<MembershipMutationResult>;
}

export type BootstrapStatus = 'idle' | 'running' | 'ready' | 'failed';

/**
 * Backoff schedule for the space bootstrap. A single transient failure used to
 * be terminal: the SpaceSwitcher effect fires once per (workspace, user,
 * permissions) change, so a 503 on get_my_spaces left the switcher reading
 * "Select a space…" until a full page reload. Bounded retries make a blip
 * self-healing without spinning on a genuine denial.
 */
const BOOTSTRAP_RETRY_DELAYS_MS = [400, 1200, 3000];

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// ── Store ──────────────────────────────────────────────────────────────────────

export const useSpaceStore = create<SpaceStoreState>()(
  persist(
    (set, get) => ({
      currentSpaceId: null,
      mySpaces: [],
      isLoading: false,
      hasFetched: false,
      bootstrapStatus: 'idle',
      bootstrapWorkspaceId: null,

      getCurrentSpace: () => {
        const { currentSpaceId, mySpaces } = get();
        return mySpaces.find(s => s.id === currentSpaceId);
      },

      getSpacesByType: (type) => get().mySpaces.filter(s => s.spaceType === type),

      getPersonalSpace: () => get().mySpaces.find(s => s.spaceType === 'personal'),

      getOrgSpace: () => get().mySpaces.find(s => s.spaceType === 'org'),

      setCurrentSpace: (spaceId) => {
        set({ currentSpaceId: spaceId });
        if (typeof sessionStorage !== 'undefined' && spaceId) {
          sessionStorage.setItem('peakPriorities_currentSpaceId', spaceId);
        }
      },

      fetchSpaces: async (workspaceId) => {
        set({ isLoading: true });
        const { spaces, ok } = await getMySpacesResult(workspaceId);
        const current = get().currentSpaceId;
        // A failed read is not an answer — keep the selection rather than
        // blanking it on a transient error. A *successful* read is an answer,
        // including an empty one: if the server says these are your spaces and
        // the selection isn't among them, the selection is dead. Keeping it
        // (as this did for an empty list) leaves a dangling id that every
        // subsequent item insert fails on — items_space_id_fkey.
        const stillValid = ok ? spaces.some(s => s.id === current) : true;
        set({
          // A failed read is not an answer — keep whatever list we already had
          // rather than blanking the switcher on a transient error.
          mySpaces: ok ? spaces : get().mySpaces,
          isLoading: false,
          hasFetched: true,
          currentSpaceId: stillValid ? current : null,
        });
      },

      bootstrapSpaces: async (workspaceId) => {
        const prev = get();
        // Already resolved (or resolving) for this workspace — the SpaceSwitcher
        // effect re-fires whenever its gates settle, and duplicate bootstraps
        // would race each other's writes.
        if (
          prev.bootstrapWorkspaceId === workspaceId &&
          (prev.bootstrapStatus === 'running' || prev.bootstrapStatus === 'ready')
        ) {
          return;
        }

        set({ bootstrapStatus: 'running', bootstrapWorkspaceId: workspaceId });

        // True only when the space list was actually read from the server. An
        // empty-but-successful read is a real answer ("no spaces yet"); a failed
        // read is not, and is what the retry loop below exists for.
        let resolved = false;

        for (let attempt = 0; attempt <= BOOTSTRAP_RETRY_DELAYS_MS.length; attempt++) {
          // The workspace changed under us — abandon this run and let the newer
          // bootstrap own the status.
          if (get().bootstrapWorkspaceId !== workspaceId) return;

          if (attempt > 0) await sleep(BOOTSTRAP_RETRY_DELAYS_MS[attempt - 1]);
          if (get().bootstrapWorkspaceId !== workspaceId) return;

          if (!(await isAuthenticated())) {
            // Session restore / token refresh can still be in flight on a cold
            // load. Retryable — never terminal.
            console.warn('[SpaceStore] bootstrapSpaces: no Supabase session yet, will retry');
            continue;
          }

          // Fast path: returning users already have their spaces, so read first
          // and only pay for the two ensure RPCs when the read comes back empty.
          const ensures = Promise.all([
            ensurePersonalSpace(workspaceId),
            ensureOrgSpace(workspaceId),
          ]).catch((err) => {
            console.warn('[SpaceStore] ensure space RPCs failed', err);
            return null;
          });

          let result = await getMySpacesResult(workspaceId);
          if (!result.ok || result.spaces.length === 0) {
            await ensures;
            result = await getMySpacesResult(workspaceId);
          }

          if (!result.ok) continue;

          if (get().bootstrapWorkspaceId !== workspaceId) return;

          // result.ok is guaranteed here, so this list is authoritative — an
          // empty one included. See fetchSpaces for why an unvalidated id must
          // not survive an authoritative answer.
          const current = get().currentSpaceId;
          const stillValid = result.spaces.some((s) => s.id === current);
          set({
            mySpaces: result.spaces,
            isLoading: false,
            hasFetched: true,
            currentSpaceId: stillValid ? current : null,
          });

          resolved = true;
          break;
        }

        if (get().bootstrapWorkspaceId !== workspaceId) return;

        if (!resolved) {
          console.error('[SpaceStore] bootstrapSpaces failed after retries for workspace', workspaceId);
          set({ bootstrapStatus: 'failed' });
          return;
        }

        const { currentSpaceId, mySpaces } = get();
        const currentSpace = mySpaces.find(s => s.id === currentSpaceId);

        if (!currentSpaceId || !currentSpace) {
          // Fall back to org space then personal — use setCurrentSpace so
          // sessionStorage and the persist layer both stay in sync.
          const defaultSpace =
            mySpaces.find(s => s.spaceType === 'org') ??
            mySpaces.find(s => s.spaceType === 'personal');
          if (defaultSpace) {
            get().setCurrentSpace(defaultSpace.id);
          } else {
            console.warn('[SpaceStore] bootstrapSpaces: no spaces available after ensure RPCs');
          }
        }

        set({ bootstrapStatus: 'ready' });
      },

      retryBootstrap: async (workspaceId) => {
        set({ bootstrapStatus: 'idle', bootstrapWorkspaceId: null });
        await get().bootstrapSpaces(workspaceId);
      },

      createSpace: async (params) => {
        const space = await createSpace(params);
        if (space) {
          // Re-fetch to get the full MySpace shape (with memberRole)
          await get().fetchSpaces(params.workspaceId);
          return get().mySpaces.find(s => s.id === space.id) ?? null;
        }
        return null;
      },

      updateSpace: async (spaceId, updates) => {
        const ok = await updateSpace(spaceId, updates);
        if (ok) {
          set(state => ({
            mySpaces: state.mySpaces.map(s =>
              s.id === spaceId ? { ...s, ...updates } : s
            ),
          }));
        }
        return ok;
      },

      deleteSpace: async (spaceId) => {
        const ok = await deleteSpace(spaceId);
        if (ok) {
          set(state => ({
            mySpaces: state.mySpaces.filter(s => s.id !== spaceId),
            currentSpaceId: state.currentSpaceId === spaceId ? null : state.currentSpaceId,
          }));
          // If we deleted the current space, fall back to org then personal
          const { currentSpaceId, mySpaces } = get();
          if (!currentSpaceId) {
            const fallback =
              mySpaces.find(s => s.spaceType === 'org') ??
              mySpaces.find(s => s.spaceType === 'personal');
            if (fallback) set({ currentSpaceId: fallback.id });
          }
        }
        return ok;
      },

      addMember: (spaceId, userId, role) => addSpaceMember(spaceId, userId, role),

      updateMemberRole: (spaceId, userId, role) => updateSpaceMemberRole(spaceId, userId, role),

      removeMember: (spaceId, userId) => removeSpaceMember(spaceId, userId),
    }),
    {
      name: 'peakPriorities_spaceStore',
      partialize: (state) => ({ currentSpaceId: state.currentSpaceId }),
    }
  )
);
